/* eslint-disable @typescript-eslint/no-explicit-any */
import { DiscoveryBlock, DiscoveryItem } from '@teko/discovery';
import { formatCurrency as libFormatCurrency } from '@teko/ds-desktop';
import { isEmptyObject, parseJsonStringSafely } from '@teko/utils';
import { NextRouter } from 'next/router';
import { DEFAULT_NUMBER_DISPLAY_FORMAT, SEARCH_ALGO } from '../constants';
import { getEnv } from './env';
export {
  getObjFromQueryString,
  isValidEmailFormat,
  isValidPhoneFormat,
} from '@teko/utils';
declare global {
  interface Window {
    MSStream: any;
  }
}

export const formatCurrency = (
  money: number | string,
  isFull = true,
  numberDisplayFormat = DEFAULT_NUMBER_DISPLAY_FORMAT,
): string => {
  const valueWithFormatCurrency = libFormatCurrency(money, {
    hideCurrencySymbol: !isFull,
  });
  if (!!numberDisplayFormat) {
    // vi-VN output uses '.' for thousands and ',' for decimals. Map both in a
    // single pass so the replacements don't collide when the tenant's
    // separators are swapped (e.g. thousand=',', decimal='.').
    return valueWithFormatCurrency.replace(/[.,]/g, (char) =>
      char === '.'
        ? numberDisplayFormat.thousandSeparator
        : numberDisplayFormat.decimalSeparator,
    );
  }
  return valueWithFormatCurrency;
};

export const saveRecentlyProduct = (sku: string, localKey: string): void => {
  const historyStorage = localStorage.getItem(localKey);
  if (!historyStorage) {
    localStorage.setItem(localKey, JSON.stringify([sku]));
    return;
  }
  let histories: string[] = parseJsonStringSafely(
    historyStorage,
    [],
  ) as string[];
  histories = histories.filter((item: string) => item !== sku);
  histories = [sku, ...histories];
  if (histories.length > 20) {
    histories.pop();
  }
  localStorage.setItem(localKey, JSON.stringify(histories));
};

export const isIOSDevice = () => {
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  return iOS;
};
export const isAndroidDevice = () => {
  const android = /(android)/i.test(navigator.userAgent);
  return android;
};
// Check if path equals ...
export const checkPathEqual = (router: NextRouter, path: string): boolean => {
  let pathname = router.asPath || router.pathname;
  pathname === '' ? '/' : pathname;
  const searchParam = pathname.indexOf('?');
  pathname = searchParam >= 0 ? pathname.substring(0, searchParam) : pathname;
  const rawPath =
    path.indexOf('?') >= 0 ? path.substring(0, path.indexOf('?')) : path;
  return pathname === rawPath;
};

// Check if path includes ...
export const checkPathInclude = (
  router: NextRouter,
  acceptedPaths: string[],
) => {
  const pathName = router.asPath || router.pathname;
  return acceptedPaths.some((path) => pathName.includes(path));
};

export const checkExpiryDateTimestamp = (
  startDate: string,
  endDate: string,
): boolean => {
  const today = new Date().getTime();
  if (
    today - new Date(parseInt(startDate) * 1000).getTime() > 0 &&
    new Date(parseInt(endDate) * 1000).getTime() - today > 0
  )
    return true;
  return false;
};

export const isHomepage = (pathname: string): boolean => {
  // Old version
  // return ['', '/'].includes(pathname);
  // This's added for supporting multi-languages & nocache query params in homepage
  return !pathname || /^(\/|)(\?.+|)$/.test(pathname);
};

export const filterBlockItems = (
  item: DiscoveryItem,
  blockList: DiscoveryBlock[],
): boolean => {
  const matchFormat = item.url.match(/^#\d+$/);
  if (!matchFormat) return false;
  const blockId = item.url.substring(1);
  const block = blockList.find((e) => e.id === blockId);
  if (!block) return false;
  return true;
};

export const getBaseDomain = () => {
  if (typeof window === 'undefined') return '';
  return `${window?.location?.protocol}/${window?.location?.host}`;
};

export const getRandomInt = (min: number, max: number) => {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled);
};

export const getSearchAlgo = () => {
  const envGenRanges = getEnv('SEARCH_ALGO_GENERATE_RANGES');
  const genRanges =
    typeof envGenRanges === 'string' ? JSON.parse(envGenRanges) : undefined;
  if (!isEmptyObject(genRanges)) {
    // random integer number between 1 & 100
    const randomValue = getRandomInt(1, 100);
    let boundary = 0;
    let result = '';
    const listAlgoKeys = Object.keys(genRanges);
    for (let i = 0; i < listAlgoKeys.length; i++) {
      boundary += genRanges[listAlgoKeys[i]];
      if (randomValue <= boundary) {
        result = listAlgoKeys[i];
        break;
      }
    }

    return result.replace(SEARCH_ALGO.REPLACE_PATTERN, '');
  }
  return '';
};

export const checkCurrentPageIsExcluded = (pathName: string) => {
  // get excluded paths from config workload
  const envExcludedUrlPaths = getEnv('SEARCH_ALGO_EXCLUDE_URL_PATHS') || '';
  const excludedUrlPaths =
    typeof envExcludedUrlPaths === 'string'
      ? envExcludedUrlPaths.split(' ')
      : [];
  // Check if current page is excluded
  return !!excludedUrlPaths.length
    ? excludedUrlPaths.some((path) => pathName.includes(path))
    : false;
};

export const getJson = (str: string) => {
  try {
    return !!str && JSON.parse(str);
  } catch (e) {
    return null;
  }
};
